import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { api, ApiRequestError } from '@/lib/api';

/** Settings / Operations: readiness, worker health and the global pause control. */
export function SettingsPage() {
  const { data } = useQuery({ queryKey: ['readiness'], queryFn: api.readiness });
  const qc = useQueryClient();
  const health = useQuery({ queryKey: ['ops-health'], queryFn: api.ops.health, refetchInterval: 5000 });
  const pause = useMutation({
    mutationFn: (paused: boolean) => api.ops.pause(paused),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops-health'] });
      toast.success('Updated');
    },
    onError: (e) => toast.error(e instanceof ApiRequestError ? e.message : 'Failed'),
  });
  const channelPause = useMutation({
    mutationFn: ({ channel, paused }: { channel: string; paused: boolean }) =>
      api.ops.pauseChannel(channel, paused),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops-health'] });
      toast.success('Updated');
    },
    onError: (e) => toast.error(e instanceof ApiRequestError ? e.message : 'Failed'),
  });
  const ops = health.data;

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">Setup readiness, credentials and health.</p>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Channel readiness</CardTitle>
          <CardDescription>Configured channels and any missing requirements.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {(data?.channels ?? []).map((c) => (
            <div key={c.channel} className="flex items-center justify-between text-sm">
              <span className="capitalize">{c.channel}</span>
              {c.enabled ? (
                c.ready ? (
                  <Badge variant="success">ready</Badge>
                ) : (
                  <Badge variant="warning">{c.missingRequirements.join(', ') || 'not ready'}</Badge>
                )
              ) : (
                <Badge variant="muted">disabled</Badge>
              )}
            </div>
          ))}
          {!data && <p className="text-sm text-muted-foreground">Loading…</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Operations</CardTitle>
            {ops && (
              <Badge variant={ops.status === 'healthy' ? 'success' : 'warning'}>{ops.status}</Badge>
            )}
          </div>
          <CardDescription>Worker health and dispatch controls.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {ops ? (
            <>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Due backlog</span>
                <span className="tabular-nums">{ops.worker.dueBacklog}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Oldest due</span>
                <span className="tabular-nums">
                  {ops.worker.oldestDueAgeSec === null ? '—' : `${ops.worker.oldestDueAgeSec}s`}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Stale leases</span>
                <span className="tabular-nums">{ops.worker.staleLeases}</span>
              </div>
              {ops.ingestion.unmatchedEvents > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Unmatched events</span>
                  <span className="tabular-nums">
                    {ops.ingestion.unmatchedEvents}
                    {ops.ingestion.oldestUnmatchedAgeSec !== null && ` · ${ops.ingestion.oldestUnmatchedAgeSec}s`}
                  </span>
                </div>
              )}
              {ops.durabilityFault && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Durability</span>
                  <Badge variant="destructive">journal fault</Badge>
                </div>
              )}
              {ops.rates
                .filter((r) => r.accepted > 0)
                .map((r) => (
                  <div key={r.channel} className="flex items-center justify-between">
                    <span className="capitalize text-muted-foreground">
                      {r.channel} complaints / bounces · {r.windowDays}d ({r.accepted})
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="tabular-nums">
                        {(r.complaintRate * 100).toFixed(2)}% / {(r.bounceRate * 100).toFixed(1)}%
                      </span>
                      {r.rateExceeded && (
                        <Badge variant={r.sampleSufficient ? 'destructive' : 'warning'}>
                          {r.sampleSufficient ? 'high' : 'watch'}
                        </Badge>
                      )}
                    </span>
                  </div>
                ))}
              {ops.paused.channels.map((channel) => (
                <div key={channel} className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2">
                    <span className="capitalize text-muted-foreground">{channel} sending</span>
                    <Badge variant="destructive">paused</Badge>
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => channelPause.mutate({ channel, paused: false })}
                    disabled={channelPause.isPending}
                  >
                    Resume {channel}
                  </Button>
                </div>
              ))}
              <Separator className="my-2" />
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">
                  Global sending {ops.paused.global ? 'paused' : 'active'}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => pause.mutate(!ops.paused.global)}
                  disabled={pause.isPending}
                >
                  {ops.paused.global ? 'Resume sending' : 'Pause sending'}
                </Button>
              </div>
            </>
          ) : (
            <p className="text-muted-foreground">Loading…</p>
          )}
        </CardContent>
      </Card>

      <Card className="border-destructive/40">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="size-4 text-destructive" />
            <CardTitle className="text-base text-destructive">Danger zone</CardTitle>
          </div>
          <CardDescription>Destructive operations are separated and explained.</CardDescription>
        </CardHeader>
        <CardContent>
          <Separator className="mb-3" />
          <p className="text-sm text-muted-foreground">
            Global pause, secret rotation and data erasure controls appear here (M4-02).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
